import { Round as ArrowUp } from '@manomano-internal/tamagoshi-icons/esm/material/ExpandLess';
import { Round as ArrowDown } from '@manomano-internal/tamagoshi-icons/esm/material/ExpandMore';
import classnames from 'classnames';
import { useRef, type FunctionComponent } from 'react';

import { Button } from '@/domains/core/tamagoshiv2/components/Button/Button';
import { H2 } from '@/domains/core/tamagoshiv2/components/Typography/Typography';
import { EconomicOperatorDisclaimer } from '@/domains/productDiscovery/EconomicOperatorDisclaimer/EconomicOperatorDisclaimer';
import { Block } from '@/domains/productDiscovery/ProductPage/b2c/components/Block/Block';
import { InfoDocs } from '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/components/InfoDocs/InfoDocs';
import {
  descriptionBlockSeeAll,
  descriptionBlockSeeLess,
  descriptionBlockSeeMore,
  descriptionBlockTitle,
} from '@/domains/productDiscovery/ProductPage/b2c/components/DescriptionBlock/translations';
import { Attributes } from '@/domains/productDiscovery/ProductPage/common/components/Attributes/Attributes';
import { Description } from '@/domains/productDiscovery/ProductPage/common/components/Description/Description';
import { useExpandedDescription } from '@/domains/productDiscovery/ProductPage/common/components/Description/hooks/useExpandedDescription';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

import styles from './styles/descriptionblock.module.scss';

interface DescriptionBlockProps {
  isTab?: boolean;
}

export const DescriptionBlock: FunctionComponent<DescriptionBlockProps> = ({
  isTab = false,
}) => {
  const {
    description,
    title,
    infoDocs,
    descriptionFileUrl,
    attributes,
    modelId,
    safetyInformationPdf,
  } = useDetail();

  const descriptionRef = useRef<HTMLDivElement | null>(null);

  const [
    { isExpanded: isExpandedDescription, hasOverflow },
    onExpandToggle,
    goToDescription,
  ] = useExpandedDescription(description || '', title, descriptionRef);
  const blockClassName = classnames(styles.container, {
    [styles.tabDescriptionContainer]: isTab,
  });

  return (
    <Block className={blockClassName} isTab={isTab}>
      {!isTab && (
        <H2 className={styles.descriptionBlockTitle}>
          {descriptionBlockTitle}
        </H2>
      )}
      <InfoDocs
        infoDocs={infoDocs}
        descriptionFileUrl={descriptionFileUrl}
        safetyInformationPdf={safetyInformationPdf}
      />
      {attributes && attributes.length > 0 && (
        <Attributes
          defaultExpanded={isTab}
          attributes={attributes}
          labels={{
            unfold: descriptionBlockSeeAll,
          }}
          behavior="unfold"
          gtmProps={{
            title: title ?? '',
          }}
        />
      )}
      <Description
        description={description}
        isExpanded={isExpandedDescription || isTab}
        modelId={modelId}
        ref={descriptionRef}
      />

      {(isTab || isExpandedDescription) && <EconomicOperatorDisclaimer />}

      {!isTab && hasOverflow && (
        <>
          <Button
            size="L"
            content={{
              icon: isExpandedDescription ? ArrowUp : ArrowDown,
              label: isExpandedDescription
                ? descriptionBlockSeeLess
                : descriptionBlockSeeMore,
            }}
            type="secondary"
            className={classnames(styles.viewMoreButton, styles.onlyDesktop)}
            onClick={(e) => {
              (e.target as HTMLElement).blur();
              onExpandToggle();
            }}
            htmlType="button"
          />
          <Button
            size="L"
            content={descriptionBlockSeeAll}
            type="secondary"
            className={classnames(styles.viewMoreButton, styles.onlyMobile)}
            onClick={goToDescription}
          />
        </>
      )}
    </Block>
  );
};

DescriptionBlock.displayName = 'DescriptionBlock';
